import * as React from "react";
import { FC } from "react";
import { CircularProgress, Tab, Tabs } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { RangeTab } from "./RangeListTypes";

/** Internal tab ID for custom filter states that don't match any predefined tab */
export const CUSTOM_TAB_ID = '__custom__';

type RangeTabsProps = {
   tabs?: RangeTab[];
   tabId: string;
   onTabChange?: (tabId: string) => void;
}

/**
 * A controlled component for displaying tabs in the RangeList component.
 *
 * When tabId is CUSTOM_TAB_ID and doesn't match any predefined tab,
 * a "Custom" tab is automatically shown.
 */
export const RangeTabs: FC<RangeTabsProps> = ({
   tabs,
   tabId,
   onTabChange
}) => {
   const handleTabChange = (_: React.SyntheticEvent, newTabId: string) => {
      if (onTabChange) {
         onTabChange(newTabId);
      }
   };

   // Hide tabs UI if no tabs exist
   if (!tabs?.length) {
      return null;
   }

   // Check if we need to show the custom tab
   const showCustomTab = tabId === CUSTOM_TAB_ID;

   return (
      <Tabs
         value={tabId}
         onChange={handleTabChange}
         style={{ marginBottom: '5px' }}
      >
         {tabs.map(tab => (
            <Tab
               key={`range_tab_${tab.id}`}
               label={<RangeTabLabel tab={tab} />}
               value={tab.id}
            />
         ))}
         {showCustomTab && (
            <Tab
               key="range_tab_custom"
               label="Custom"
               value={CUSTOM_TAB_ID}
            />
         )}
      </Tabs>
   );
};

type RangeTabLabelProps = {
   tab: RangeTab;
}

const RangeTabLabel: FC<RangeTabLabelProps> = ({ tab }) => {
   const classes = useStyles();

   return (
      <div>
         {tab.label}
         {tab.showCount && (
            <span className={classes.badge}>
               {tab.count !== undefined ? tab.count : <CircularProgress sx={{ mt: '4px', color: 'white' }} size={10} />}
            </span>
         )}
      </div>
   );
};

const useStyles = makeStyles({
   badge: {
      borderRadius: '50%',
      backgroundColor: 'rgb(255, 83, 118)',
      display: 'inline-block',
      color: 'white',
      paddingLeft: '5px',
      paddingRight: '5px',
      marginLeft: '5px'
   }
});
